import { useMemo } from "react";
import type { PlanEvent, Task } from "../../../shared/types";
import { getAllCategories } from "../../../shared/stores/category-store";

interface Stats {
  readonly totalTasks: number;
  readonly completionRate: number;
  readonly completedThisWeek: number;
  readonly categoryCounts: Record<string, number>;
  readonly statusCounts: Record<string, number>;
  readonly eventsThisWeek: number;
  readonly streak: number;
}

function toDateStr(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function calculateStreak(tasks: Task[], events: PlanEvent[]): number {
  const activeDates = new Set<string>();

  for (const task of tasks) {
    if (!task.archived) {
      const date = toDateStr(new Date(task.createdAt));
      activeDates.add(date);
    }
  }

  for (const event of events) {
    activeDates.add(event.date);
  }

  if (activeDates.size === 0) return 0;

  let streak = 0;
  const currentDate = new Date();

  for (let i = 0; i < 365; i++) {
    const dateStr = toDateStr(currentDate);
    if (activeDates.has(dateStr)) {
      streak++;
      currentDate.setDate(currentDate.getDate() - 1);
    } else {
      break;
    }
  }

  return streak;
}

function computeStats(tasks: Task[], events: PlanEvent[]): Stats {
  const now = Date.now();
  const activeTasks = tasks.filter((t) => !t.archived);
  const totalTasks = activeTasks.length;
  const completedTasks = activeTasks.filter((t) => t.status === "done").length;
  const completionRate =
    totalTasks === 0 ? 0 : Math.round((completedTasks / totalTasks) * 100);

  const sevenDaysAgo = now - 7 * 24 * 60 * 60 * 1000;
  const completedThisWeek = activeTasks.filter(
    (t) => t.status === "done" && t.createdAt >= sevenDaysAgo,
  ).length;

  const allCategories = getAllCategories();
  const categoryCounts: Record<string, number> = {};
  for (const cat of allCategories) {
    categoryCounts[cat] = activeTasks.filter((t) => t.category === cat).length;
  }

  const statusCounts = {
    todo: activeTasks.filter((t) => t.status === "todo").length,
    doing: activeTasks.filter((t) => t.status === "doing").length,
    done: completedTasks,
  };

  const eventsThisWeek = events.filter((e) => {
    const eventDate = new Date(e.date).getTime();
    return eventDate >= sevenDaysAgo && eventDate <= now;
  }).length;

  const streak = calculateStreak(tasks, events);

  return {
    totalTasks,
    completionRate,
    completedThisWeek,
    categoryCounts,
    statusCounts,
    eventsThisWeek,
    streak,
  };
}

export function useStatsCalculation(tasks: Task[], events: PlanEvent[]): Stats {
  return useMemo(() => computeStats(tasks, events), [tasks, events]);
}
